import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useGiftCards } from '@/features/gift-cards';

const LIMIT = 10;

/** Gift cards and store credit linked to the shopper, read from the gift card list by customer. */
export function CustomerGiftCardsCard({
  storeId,
  customerId,
}: {
  storeId: string;
  customerId: string;
}) {
  const { t } = useTranslation(['customers', 'giftCards']);
  const format = useFormat();
  const query = useGiftCards(storeId, { customerId, limit: LIMIT });
  const cards = query.data?.items ?? [];
  const balance = cards
    .filter((card) => card.status === 'active')
    .reduce((sum, card) => sum + card.balance.amount, 0);
  const currency = cards[0]?.balance.currency;

  return (
    <Card className="gap-3" data-tour="customers.detail.gift-cards">
      <CardHeader>
        <CardTitle>{t('customers:giftCards.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <Skeleton className="h-16" />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : cards.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('customers:giftCards.empty')}</p>
        ) : (
          <>
            {currency ? (
              <p className="mb-3 text-sm" data-testid="customer-store-credit">
                {t('customers:giftCards.balance', {
                  amount: format.money({ amount: balance, currency }),
                })}
              </p>
            ) : null}
            <Table aria-label={t('customers:giftCards.title')}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('giftCards:columns.code')}</TableHead>
                  <TableHead className="text-right">{t('giftCards:columns.balance')}</TableHead>
                  <TableHead>{t('giftCards:columns.status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cards.map((card) => (
                  <TableRow key={card.id}>
                    <TableCell>
                      <Link
                        to="/s/$storeId/gift-cards/$giftCardId"
                        params={{ storeId, giftCardId: card.id }}
                        className="font-mono text-[13px] font-medium hover:underline"
                      >
                        {t('giftCards:masked', { last4: card.last4 })}
                      </Link>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {format.money(card.balance)}
                      <span className="text-muted-foreground">
                        {' '}
                        {t('giftCards:balance.of', { initial: format.money(card.initial) })}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{t(`giftCards:status.${card.status}`)}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {query.data.nextCursor ? (
              <p className="text-muted-foreground mt-2 text-xs">
                {t('customers:giftCards.more', { count: LIMIT })}
              </p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
